import { signIn as cognitoSignIn, signOut as cognitoSignOut, confirmSignIn, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

import {
    type AuthChallenge,
    type AuthClient,
    AuthError,
    type AuthErrorCode,
    type AuthStateListener,
    type AuthUser,
    type CompleteSignInInput,
    type LoginCredentials,
    type SignInResult,
} from './AuthClient';

type CognitoSession = Awaited<ReturnType<typeof fetchAuthSession>>;
type TokenPayload = NonNullable<CognitoSession['tokens']>['accessToken']['payload'];
type SignInOutput = Awaited<ReturnType<typeof cognitoSignIn>>;
type SignInStep = SignInOutput['nextStep']['signInStep'];

const challengeKindsByStep: Partial<Record<SignInStep, AuthChallenge['kind']>> = {
    CONFIRM_SIGN_IN_WITH_SMS_CODE: 'mfaCode',
    CONFIRM_SIGN_IN_WITH_TOTP_CODE: 'mfaCode',
    CONFIRM_SIGN_IN_WITH_EMAIL_CODE: 'mfaCode',
    CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED: 'newPasswordRequired',
};

/** Cognito and Amplify error names, mapped onto the provider independent codes the app reacts to. */
const errorCodesByName: Record<string, AuthErrorCode> = {
    EmptySignInPassword: 'invalidCredentials',
    EmptySignInUsername: 'invalidCredentials',
    NotAuthorizedException: 'invalidCredentials',
    UserNotConfirmedException: 'invalidCredentials',
    UserNotFoundException: 'invalidCredentials',
    PasswordResetRequiredException: 'passwordResetRequired',
    NetworkError: 'network',
    InternalErrorException: 'unavailable',
    LimitExceededException: 'unavailable',
    TooManyRequestsException: 'unavailable',
};

/** Hub events after which the session may differ from what the application last recorded. */
const sessionChangingEvents: ReadonlySet<string> = new Set([
    'signedIn',
    'signedOut',
    'tokenRefresh',
    'tokenRefresh_failure',
]);

const USER_ALREADY_AUTHENTICATED = 'UserAlreadyAuthenticatedException';

const nameOf = (error: unknown): string => (error instanceof Error ? error.name : '');

const readString = (payload: TokenPayload, key: string): string | undefined => {
    const value = payload[key];

    return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
};

/** Cognito sends groups as a JSON array, but a pre token generation trigger may send a string. */
const readStringList = (payload: TokenPayload, key: string): string[] => {
    const value = payload[key];

    if (Array.isArray(value)) {
        return value.filter((entry): entry is string => typeof entry === 'string' && entry.trim() !== '');
    }

    if (typeof value === 'string') {
        return value
            .split(',')
            .map(entry => entry.trim())
            .filter(entry => entry !== '');
    }

    return [];
};

const splitFullName = (fullName: string | undefined): [string, string] => {
    if (fullName === undefined) {
        return ['', ''];
    }

    const [first = '', ...rest] = fullName.split(/\s+/);

    return [first, rest.join(' ')];
};

const toAuthUser = (session: CognitoSession): AuthUser | null => {
    const { idToken, accessToken } = session.tokens ?? {};

    if (accessToken === undefined) {
        return null;
    }

    const claims = idToken?.payload ?? accessToken.payload;
    const id = readString(claims, 'sub') ?? session.userSub;

    if (id === undefined) {
        return null;
    }

    const [fallbackFirstName, fallbackLastName] = splitFullName(readString(claims, 'name'));

    return {
        id,
        firstName: readString(claims, 'given_name') ?? fallbackFirstName,
        lastName: readString(claims, 'family_name') ?? fallbackLastName,
        email: readString(claims, 'email') ?? readString(claims, 'cognito:username') ?? '',
        roles: readStringList(claims, 'cognito:groups'),
    };
};

export class CognitoAuthClient implements AuthClient {
    public async getCurrentUser(): Promise<AuthUser | null> {
        const session = await this.readSession();

        return session.outcome === 'read' ? session.user : null;
    }

    public async getAccessToken(): Promise<string | null> {
        try {
            const session = await fetchAuthSession();

            return session.tokens?.accessToken?.toString() ?? null;
        } catch {
            return null;
        }
    }

    public async signIn(credentials?: LoginCredentials): Promise<SignInResult> {
        if (credentials === undefined) {
            throw new AuthError('notSupported', 'Cognito sign in requires an email and a password');
        }

        try {
            return await this.resolveSignInStep(await this.startSignIn(credentials), credentials.email);
        } catch (error) {
            throw toCognitoAuthError(error);
        }
    }

    public async completeSignIn(input: CompleteSignInInput): Promise<SignInResult> {
        if (input.kind === 'redirectCallback') {
            throw new AuthError('notSupported', 'This Cognito client signs in with credentials, not with a redirect');
        }

        try {
            const output = await confirmSignIn({ challengeResponse: input.value });

            return await this.resolveSignInStep(output, input.challenge.token);
        } catch (error) {
            throw toCognitoAuthError(error);
        }
    }

    public async signOut(): Promise<void> {
        try {
            await cognitoSignOut();
        } catch (error) {
            throw toCognitoAuthError(error);
        }
    }

    public subscribe(listener: AuthStateListener): () => void {
        return Hub.listen('auth', ({ payload }) => {
            if (!sessionChangingEvents.has(payload.event)) {
                return;
            }

            void this.readSession().then(session => {
                if (session.outcome === 'read') {
                    listener(session.user);
                }
            });
        });
    }

    private async readSession(): Promise<{ outcome: 'read'; user: AuthUser | null } | { outcome: 'unavailable' }> {
        try {
            return { outcome: 'read', user: toAuthUser(await fetchAuthSession()) };
        } catch {
            return { outcome: 'unavailable' };
        }
    }

    private async startSignIn({ email, password }: LoginCredentials): Promise<SignInOutput> {
        const input = { username: email.trim(), password };

        try {
            return await cognitoSignIn(input);
        } catch (error) {
            if (nameOf(error) !== USER_ALREADY_AUTHENTICATED) {
                throw error;
            }

            // A session left behind by a previous sign in blocks this one. Replacing it is the intent.
            await cognitoSignOut();

            return cognitoSignIn(input);
        }
    }

    private async resolveSignInStep(output: SignInOutput, email: string): Promise<SignInResult> {
        const step = output.nextStep.signInStep;

        if (step === 'DONE') {
            const user = await this.getCurrentUser();

            if (user === null) {
                throw new AuthError('unknown', 'Cognito completed the sign in but produced no session');
            }

            return { status: 'authenticated', user };
        }

        if (step === 'RESET_PASSWORD') {
            throw new AuthError('passwordResetRequired', 'The password must be reset before signing in');
        }

        const kind = challengeKindsByStep[step];

        if (kind === undefined) {
            throw new AuthError('notSupported', `Cognito asked for an unsupported sign in step: ${step}`);
        }

        return { status: 'challenge', challenge: { kind, token: email } };
    }
}

const toCognitoAuthError = (error: unknown): AuthError => {
    if (error instanceof AuthError) {
        return error;
    }

    const code = errorCodesByName[nameOf(error)];

    if (code !== undefined) {
        return new AuthError(code, error instanceof Error ? error.message : undefined, { cause: error });
    }

    return new AuthError('unknown', 'Unexpected Cognito authentication error', { cause: error });
};
